import React from 'react';
import { Link } from 'react-router-dom';
import { XMarkIcon, ExclamationTriangleIcon, InformationCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { parseErrorResponse, formatErrorForDisplay } from '../utils/twilioFieldMapping';

/**
 * Enhanced error modal for Twilio submission failures
 * Displays field-specific errors with actionable guidance
 */

// Sanitize Twilio error code before using it in URLs
function sanitizeErrorCode(code) {
  if (code === undefined || code === null) {
    return null;
  }
  const str = String(code);
  // Twilio error codes are 5 digits (e.g., "30001"). Anything else, such as an
  // HTTP status like 400, has no Twilio docs page, so don't link it.
  const match = str.match(/^[0-9]{5}$/);
  return match ? str : null;
}

export default function TwilioErrorModal({ errorResponse, onClose, onViewSubmission, onRecreate, submissionTypeLabel, canEditConfigs = false }) {
  if (!errorResponse) return null;

  const parsedError = parseErrorResponse(errorResponse);
  const hasFieldError = parsedError.fieldError && parsedError.fieldInfo;
  const safeErrorCode = sanitizeErrorCode(parsedError.code);

  // Some Twilio fields (sample message, use case, opt-in type, volume) come from
  // the shared Submission Configs, not the submission. Editing the submission
  // cannot fix those, so point at the config instead.
  const isConfigField = hasFieldError && parsedError.fieldInfo.source === 'config';
  const configName = submissionTypeLabel ? `${submissionTypeLabel} config` : 'config for this submission type';

  // The backend flags a rejected verification whose 7-day edit window has closed
  // as recoverable. Surface the recovery action right where the failure appears
  // so the user isn't left at a dead end.
  const canRecreate = errorResponse.canRecreate === true && typeof onRecreate === 'function';

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-red-50 flex items-start justify-between">
          <div className="flex items-start space-x-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-red-900">
                {parsedError.title}
              </h3>
              {parsedError.code && (
                <p className="text-sm text-red-700 mt-1">
                  Error Code: {parsedError.code}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {/* Main Error Message */}
          <div className="mb-4">
            <p className="text-gray-900 font-medium">
              {parsedError.message}
            </p>
          </div>

          {/* Field-Specific Error */}
          {hasFieldError && (
            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <InformationCircleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-yellow-900 mb-2">
                    Issue with {parsedError.fieldInfo.label}
                  </h4>
                  <p className="text-sm text-yellow-800 mb-3">
                    {parsedError.actionableGuidance}
                  </p>
                  <div className="bg-white px-3 py-2 rounded border border-yellow-300">
                    <p className="text-xs font-mono text-gray-600">
                      Field: <span className="font-semibold">{parsedError.fieldError}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Original Error Message (if different) */}
          {parsedError.originalMessage && parsedError.originalMessage !== parsedError.message && (
            <div className="mb-4">
              <details className="group">
                <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900 flex items-center space-x-2">
                  <span>Technical Details</span>
                  <svg
                    className="h-4 w-4 transform transition-transform group-open:rotate-90"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </summary>
                <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded text-sm text-gray-700 font-mono">
                  {parsedError.originalMessage}
                </div>
              </details>
            </div>
          )}

          {/* What to Do Next */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center space-x-2">
              <CheckCircleIcon className="h-5 w-5" />
              <span>What to Do Next</span>
            </h4>
            <ol className="list-decimal list-inside text-sm text-blue-800 space-y-1">
              {isConfigField ? (
                canEditConfigs ? (
                  <>
                    <li>Open <strong>Submission Configs</strong> and edit the {configName}</li>
                    <li>Correct the <strong>{parsedError.fieldInfo.label}</strong> field and save</li>
                    <li>Come back and submit to Twilio again</li>
                  </>
                ) : (
                  <>
                    <li>Ask an admin to correct the <strong>{parsedError.fieldInfo.label}</strong> in the {configName} (Submission Configs)</li>
                    <li>Once it is saved, submit to Twilio again. No change to this submission is needed.</li>
                  </>
                )
              ) : hasFieldError ? (
                <>
                  <li>Review and correct the <strong>{parsedError.fieldInfo.label}</strong> field</li>
                  <li>Ensure the value follows the format guidance above</li>
                  <li>Save your changes</li>
                  <li>Try submitting to Twilio again</li>
                </>
              ) : (
                <>
                  <li>Review all required fields for accuracy</li>
                  <li>Ensure all URLs are valid and accessible</li>
                  <li>Verify business registration information is correct</li>
                  <li>Save your changes and try again</li>
                </>
              )}
            </ol>
          </div>

          {/* More Info Link */}
          {safeErrorCode && (
            <div className="mt-4 text-sm text-gray-600">
              <a
                href={`https://www.twilio.com/docs/api/errors/${safeErrorCode}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                View Twilio documentation for error {safeErrorCode} →
              </a>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
          {isConfigField && canEditConfigs && (
            <Link
              to="/submission-configs"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
            >
              Open Submission Configs
            </Link>
          )}
          {onViewSubmission && !isConfigField && (
            <button
              onClick={() => {
                onClose();
                onViewSubmission();
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
            >
              Edit Submission
            </button>
          )}
          {canRecreate && (
            <button
              onClick={() => {
                onClose();
                onRecreate();
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
            >
              Recreate Verification
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
